import type { IEventStore } from "applesauce-core/event-store";
import type { Filter } from "applesauce-core/helpers";
import type { RelayPool } from "applesauce-relay";
import type { NostrEvent } from "nostr-tools";
import type { Subscription } from "rxjs";

import { resilientSubscription } from "@/lib/resilientSubscription";
import { normalizeUrl } from "@/lib/url";

export interface PreWriteRelaySnapshot {
  events: NostrEvent[];
  completedRelays: string[];
  quorumRelays: string[];
}

export interface PreWriteRelaySnapshotOptions {
  pool: RelayPool;
  /** Optional destination for candidates; omit for encrypted/private data. */
  store?: IEventStore;
  filters: readonly Filter[];
  contributorRelays?: readonly string[];
  voterRelays: readonly string[];
  requiredVotes: number;
  maxSettleTime: number;
  voterLabel: string;
}

export class PreWriteRelayQuorumError extends Error {
  constructor(
    readonly voterLabel: string,
    readonly completedVotes: number,
    readonly requiredVotes: number,
    readonly voterCount: number,
  ) {
    super(
      `Could not complete the ${voterLabel} freshness check: ${completedVotes} of ${requiredVotes} required relays returned EOSE (${voterCount} configured).`,
    );
    this.name = "PreWriteRelayQuorumError";
  }
}

function normalizedRelays(relays: readonly string[]): string[] {
  return [...new Set(relays.map(normalizeUrl))].sort();
}

/** One third of a user's declared outboxes, bounded to one through three. */
export function requiredPersonalOutboxVotes(outboxCount: number): number {
  if (outboxCount <= 0) return 0;
  return Math.min(3, Math.max(1, Math.ceil(outboxCount / 3)));
}

/** Half of configured fallback relays, bounded to one through three. */
export function requiredFallbackVotes(fallbackCount: number): number {
  if (fallbackCount <= 0) return 0;
  return Math.min(3, Math.max(1, Math.ceil(fallbackCount / 2)));
}

/**
 * Fetch exact filters until a named voter set reaches a real-EOSE quorum.
 *
 * Contributor relays can supply a newer matching event, but neither their
 * EOSE nor their failure affects completion. Voter failures are non-votes;
 * they only block the operation when too few voters answer by the deadline.
 */
export function requestPreWriteRelaySnapshot({
  pool,
  store,
  filters,
  contributorRelays = [],
  voterRelays,
  requiredVotes,
  maxSettleTime,
  voterLabel,
}: PreWriteRelaySnapshotOptions): Promise<PreWriteRelaySnapshot> {
  const voters = normalizedRelays(voterRelays);
  const voterSet = new Set(voters);
  const queryRelays = normalizedRelays([...voters, ...contributorRelays]);

  if (
    !Number.isInteger(requiredVotes) ||
    requiredVotes < 1 ||
    requiredVotes > voters.length
  ) {
    return Promise.reject(
      new PreWriteRelayQuorumError(voterLabel, 0, requiredVotes, voters.length),
    );
  }

  return new Promise((resolve, reject) => {
    const completed = new Set<string>();
    const completedVoters = new Set<string>();
    const events = new Map<string, NostrEvent>();
    const resources: {
      subscription?: Subscription;
      timer?: ReturnType<typeof setTimeout>;
    } = {};
    let finished = false;

    const finish = (
      snapshot?: PreWriteRelaySnapshot,
      error?: PreWriteRelayQuorumError,
    ) => {
      if (finished) return;
      finished = true;
      if (resources.timer) clearTimeout(resources.timer);
      resources.subscription?.unsubscribe();
      if (error) reject(error);
      else if (snapshot) resolve(snapshot);
    };

    const fail = () =>
      finish(
        undefined,
        new PreWriteRelayQuorumError(
          voterLabel,
          completedVoters.size,
          requiredVotes,
          voters.length,
        ),
      );

    const tryFinish = () => {
      if (completedVoters.size < requiredVotes) return;
      finish({
        events: [...events.values()],
        completedRelays: [...completed].sort(),
        quorumRelays: [...completedVoters].sort().slice(0, requiredVotes),
      });
    };

    resources.timer = setTimeout(fail, Math.max(0, maxSettleTime));

    const subscription = resilientSubscription(
      pool,
      queryRelays,
      [...filters],
      {
        autoClose: true,
        reconnect: false,
        gapFill: false,
        paginate: false,
        settle: false,
        retryCount: 0,
        onRelayEose: (relay) => {
          if (finished) return;
          const normalized = normalizeUrl(relay);
          completed.add(normalized);
          if (voterSet.has(normalized)) completedVoters.add(normalized);
          tryFinish();
        },
      },
    ).subscribe({
      next: (response) => {
        if (finished || response === "EOSE") return;
        events.set(response.id, response);
        store?.add(response);
      },
      error: fail,
      complete: () => {
        if (!finished) fail();
      },
    });
    resources.subscription = subscription;
    if (finished) subscription.unsubscribe();
  });
}
